import {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useId,
} from "react";
import { Input, InputChangeEvent } from "@progress/kendo-react-inputs";
import { FiSearch } from "react-icons/fi";
import { IoCloseOutline } from "react-icons/io5";
import SpinbuttonDateInput from "@/components/shared/forms/SpinbuttonDateInput";
import {
  DOBRange,
  isDOBOutOfRange,
  getCurrentProgramYear,
  getValidDOBRange,
} from "@/utils/dob-validation";
import { useCoreData } from "@/queries";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

export type ChildSearchFormState = {
  csppid: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
};

type ChildSearchFormProps = {
  preschools: PreschoolSummary[];
  disabled: boolean;
  onSearch: (filters: ChildSearchFormState) => void;
  onCancel: () => void;
  searchError?: string;
  onClearSearchError?: () => void;
  searchCriteria?: ChildSearchFormState | null;
  dobRange?: DOBRange;
};

export type ChildSearchFormRef = {
  handleSearch: () => void;
  handleCancel: () => void;
};

export const ChildSearchForm = forwardRef<
  ChildSearchFormRef,
  ChildSearchFormProps
>(function ChildSearchForm(
  {
    disabled,
    onSearch,
    onCancel,
    searchError,
    onClearSearchError,
    searchCriteria,
    dobRange,
  },
  ref
) {
  const baseId = useId();
  const csppidId = `${baseId}-csppid`;
  const firstNameId = `${baseId}-first-name`;
  const lastNameId = `${baseId}-last-name`;
  const dobId = `${baseId}-dob`;
  const dobLabelId = `${baseId}-dob-label`;
  const option1HeadingId = `${baseId}-option1-heading`;
  const option2HeadingId = `${baseId}-option2-heading`;
  const option2InstructionsId = `${baseId}-option2-instructions`;

  const [formState, setFormState] = useState<ChildSearchFormState>({
    csppid: searchCriteria?.csppid || "",
    firstName: searchCriteria?.firstName || "",
    lastName: searchCriteria?.lastName || "",
    dateOfBirth: searchCriteria?.dateOfBirth || "",
  });
  const [validationError, setValidationError] = useState<string>("");

  const [lastSearchOption, setLastSearchOption] = useState<
    "option1" | "option2" | null
  >(null);
  const [searchedCsppid, setSearchedCsppid] = useState<string>("");
  const [dobResetKey, setDobResetKey] = useState(0);
  const [dobIncomplete, setDobIncomplete] = useState(false);

  const coreData = useCoreData();
  const computedDobRange = getValidDOBRange(
    getCurrentProgramYear(coreData.programYears ?? []),
    coreData.statutoryAges ?? []
  );
  const effectiveDobRange = dobRange ?? computedDobRange;

  const option1Active = formState.csppid.trim().length > 0;
  const option2Active =
    formState.firstName.trim().length > 0 ||
    formState.lastName.trim().length > 0 ||
    formState.dateOfBirth.trim().length > 0 ||
    dobIncomplete;

  useEffect(() => {
    if (searchCriteria) {
      setFormState({
        csppid: searchCriteria.csppid || "",
        firstName: searchCriteria.firstName || "",
        lastName: searchCriteria.lastName || "",
        dateOfBirth: searchCriteria.dateOfBirth || "",
      });
      setDobResetKey((k) => k + 1);
      setDobIncomplete(false);
    }
  }, [searchCriteria]);

  const handleChange = (key: keyof ChildSearchFormState, value: string) => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
    if (validationError) {
      setValidationError("");
    }
    if (searchError && onClearSearchError) {
      onClearSearchError();
    }
  };

  const handleClearOption1 = () => {
    setFormState((prev) => ({
      ...prev,
      csppid: "",
    }));
    if (validationError) {
      setValidationError("");
    }
    if (searchError && onClearSearchError) {
      onClearSearchError();
    }
  };

  const handleClearOption2 = () => {
    setFormState((prev) => ({
      ...prev,
      firstName: "",
      lastName: "",
      dateOfBirth: "",
    }));
    setDobResetKey((k) => k + 1);
    setDobIncomplete(false);
    if (validationError) {
      setValidationError("");
    }
    if (searchError && onClearSearchError) {
      onClearSearchError();
    }
  };

  const validateForm = (): boolean => {
    const hasCSPPID = formState.csppid.trim().length > 0;
    const hasFirstName = formState.firstName.trim().length > 0;
    const hasLastName = formState.lastName.trim().length > 0;
    const hasDOB = formState.dateOfBirth.trim().length === 10;

    if (formState.dateOfBirth && formState.dateOfBirth < "1900-01-01") {
      setValidationError("Please enter a valid Date of Birth.");
      return false;
    }

    if (
      formState.dateOfBirth.trim().length === 10 &&
      effectiveDobRange &&
      isDOBOutOfRange(formState.dateOfBirth, effectiveDobRange)
    ) {
      setValidationError(
        "Date Of Birth is not within the eligible range for the current program year."
      );
      return false;
    }

    if (hasCSPPID) {
      return true;
    }

    if (hasFirstName && hasLastName && hasDOB) {
      return true;
    }

    if (
      hasFirstName ||
      hasLastName ||
      formState.dateOfBirth.trim().length > 0 ||
      dobIncomplete
    ) {
      if (dobIncomplete) {
        setValidationError(
          "Please enter a complete Date of Birth (MM/DD/YYYY)."
        );
      } else {
        setValidationError(
          "Please enter First Name, Last Name, and Date of Birth together."
        );
      }
      return false;
    }

    setValidationError(
      "Please enter either CSPPID or First Name, Last Name, and Date of Birth."
    );
    return false;
  };

  const handleSearchClick = () => {
    if (!disabled && validateForm()) {
      if (formState.csppid.trim().length > 0) {
        setLastSearchOption("option1");
        setSearchedCsppid(formState.csppid.trim());
      } else {
        setLastSearchOption("option2");
        setSearchedCsppid("");
      }
      onSearch(formState);
    }
  };

  const handleCancelClick = () => {
    onCancel();
  };

  useImperativeHandle(ref, () => ({
    handleSearch: handleSearchClick,
    handleCancel: handleCancelClick,
  }));

  return (
    <div style={{ padding: "0 24px 24px", background: "white" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "24px",
          gap: "12px",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            background: "#1e5a78",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FiSearch size={18} color="white" aria-hidden="true" />
        </div>
        <p
          style={{
            margin: 0,
            fontSize: "14px",
            color: "#595959",
            lineHeight: "1.5",
          }}
        >
          Search for a child record match already in the CAPSDAC system.
        </p>
      </div>

      {/* Option 1: CSPPID */}
      <div
        role="group"
        aria-labelledby={option1HeadingId}
        style={{
          marginBottom: "24px",
          padding: "16px",
          background: option1Active ? "#f8f9fa" : "transparent",
          borderLeft: option1Active
            ? "4px solid #1e5a78"
            : "4px solid transparent",
          borderRadius: "4px",
          opacity: option2Active ? 0.3 : 1,
          pointerEvents: option2Active ? "none" : "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <span
            id={option1HeadingId}
            style={{
              fontSize: "13px",
              color: "#595959",
              fontWeight: "500",
            }}
          >
            Option 1
          </span>
          {option1Active && (
            <button
              type="button"
              onClick={handleClearOption1}
              disabled={disabled}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                background: "none",
                border: "none",
                color: "#595959",
                fontSize: "13px",
                cursor: "pointer",
                padding: "4px",
              }}
            >
              <IoCloseOutline size={16} aria-hidden="true" />
              Clear
            </button>
          )}
        </div>
        <label
          htmlFor={csppidId}
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "8px",
            fontSize: "14px",
            color: "#333",
            gap: "6px",
            fontWeight: option1Active ? "600" : "normal",
          }}
        >
          Enter CSPPID
          <FiSearch size={14} color="#6c757d" aria-hidden="true" />
        </label>
        <Input
          id={csppidId}
          style={{
            width: "100%",
            maxWidth: "340px",
            border: option1Active ? "2px solid #1e5a78" : undefined,
          }}
          value={formState.csppid}
          onChange={(e: InputChangeEvent) =>
            handleChange("csppid", e.value || "")
          }
          placeholder="Enter CSPPID"
          disabled={disabled || option2Active}
        />
        {/* Search error for CSPPID (Option 1) */}
        {searchError && lastSearchOption === "option1" && (
          <div
            role="alert"
            style={{
              padding: "12px 16px",
              background: "#f8d7da",
              border: "1px solid #f5c2c7",
              borderRadius: "4px",
              marginTop: "12px",
              fontSize: "14px",
              color: "#842029",
            }}
          >
            {searchError.toLowerCase().includes("no match") ||
            searchError.toLowerCase().includes("not found")
              ? `No matches found for CSPPID "${searchedCsppid}". Please search again using First Name, Last Name, and Date of Birth.`
              : searchError}
          </div>
        )}{" "}
      </div>

      <div style={{ position: "relative", margin: "32px 0" }}>
        <hr
          style={{ margin: 0, border: "none", borderTop: "2px solid #aaa" }}
        />
        <span
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "white",
            padding: "0 16px",
            color: "#595959",
            fontSize: "14px",
            fontWeight: "600",
          }}
        >
          OR
        </span>
      </div>

      {/* Option 2: Name and DOB */}
      <div
        role="group"
        aria-labelledby={`${option2HeadingId} ${option2InstructionsId}`}
        style={{
          marginBottom: "24px",
          padding: "16px",
          background: option2Active ? "#f8f9fa" : "transparent",
          borderLeft: option2Active
            ? "4px solid #1e5a78"
            : "4px solid transparent",
          borderRadius: "4px",
          opacity: option1Active ? 0.3 : 1,
          pointerEvents: option1Active ? "none" : "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <span
            id={option2HeadingId}
            style={{
              fontSize: "13px",
              color: "#595959",
              fontWeight: "500",
            }}
          >
            Option 2
          </span>
          {option2Active && (
            <button
              type="button"
              onClick={handleClearOption2}
              disabled={disabled}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                background: "none",
                border: "none",
                color: "#595959",
                fontSize: "13px",
                cursor: "pointer",
                padding: "4px",
              }}
            >
              <IoCloseOutline size={16} aria-hidden="true" />
              Clear
            </button>
          )}
        </div>

        <span
          id={option2InstructionsId}
          style={{
            display: "block",
            marginBottom: "16px",
            fontSize: "14px",
            color: "#333",
            fontWeight: option2Active ? "600" : "normal",
          }}
        >
          Enter First Name, Last Name, Date of Birth
        </span>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "16px",
          }}
        >
          <div>
            <label
              htmlFor={firstNameId}
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                color: "#595959",
              }}
            >
              First Name
            </label>
            <Input
              id={firstNameId}
              style={{ width: "100%" }}
              value={formState.firstName}
              onChange={(e: InputChangeEvent) =>
                handleChange("firstName", e.value || "")
              }
              placeholder="Enter first name"
              disabled={disabled || option1Active}
            />
          </div>

          <div>
            <label
              htmlFor={lastNameId}
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                color: "#595959",
              }}
            >
              Last Name
            </label>
            <Input
              id={lastNameId}
              style={{ width: "100%" }}
              value={formState.lastName}
              onChange={(e: InputChangeEvent) =>
                handleChange("lastName", e.value || "")
              }
              placeholder="Enter last name"
              disabled={disabled || option1Active}
            />
          </div>

          <div>
            <label
              id={dobLabelId}
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                color: "#595959",
              }}
            >
              Date of Birth
              <span className="visually-hidden"> format mm/dd/yyyy</span>
            </label>
            <SpinbuttonDateInput
              key={dobResetKey}
              id={dobId}
              labelledBy={dobLabelId}
              value={formState.dateOfBirth}
              onChange={(value) => handleChange("dateOfBirth", value)}
              onIncompleteChange={setDobIncomplete}
              disabled={disabled || option1Active}
            />
          </div>
        </div>

        {/* Search error for Name/DOB (Option 2) */}
        {searchError && lastSearchOption === "option2" && (
          <div
            role="alert"
            style={{
              padding: "12px 16px",
              background: "#f8d7da",
              border: "1px solid #f5c2c7",
              borderRadius: "4px",
              marginTop: "12px",
              fontSize: "14px",
              color: "#842029",
            }}
          >
            {searchError}
          </div>
        )}
      </div>

      {validationError && (
        <div
          role="alert"
          style={{
            padding: "12px 16px",
            background: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "4px",
            marginBottom: "16px",
            fontSize: "14px",
            color: "#856404",
          }}
        >
          {validationError}
        </div>
      )}
    </div>
  );
});
